import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Overlay, { OverlayComponentProps, Transition } from "@insite/mobius/Overlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import VisuallyHidden from "@insite/mobius/VisuallyHidden/VisuallyHidden";
import * as React from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const CloseModalIcon: React.FC = () => {
    return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.6689 9.77007L19.7645 1.67444C20.0966 1.28747 20.0746 0.708878 19.7133 0.348753C19.3532 -0.0125594 18.7746 -0.0345594 18.3876 0.297484L10.292 8.39311L2.19633 0.287484C1.81302 -0.0958281 1.19293 -0.0958281 0.809616 0.287484C0.426303 0.670797 0.426303 1.29089 0.809616 1.6742L8.91524 9.76983L0.809616 17.8655C0.603316 18.0425 0.480022 18.2976 0.469053 18.5698C0.458067 18.8408 0.561828 19.1057 0.754697 19.2973C0.946347 19.4902 1.21123 19.594 1.48223 19.583C1.75444 19.572 2.00957 19.4487 2.18657 19.2424L10.2922 11.1468L18.3878 19.2424C18.7748 19.5744 19.3534 19.5525 19.7135 19.1911C20.0748 18.831 20.0968 18.2524 19.7648 17.8655L11.6689 9.77007Z" fill="#006AE3"/>
        </svg>
    );
};

export type SizeVariant = "small" | "medium" | "large";

export interface ModalPresentationProps {
    /** CSS strings or styled-components functions to be injected into nested components. These will override the theme defaults.
     * @themable
     */
    cssOverrides?: {
        scrim?: StyledProp<any>;
        modalContainer?: StyledProp<ModalProps>;
        modalBody?: StyledProp<ModalProps>;
        modalTitle?: StyledProp<ModalProps>;
        modalContent?: StyledProp<ModalProps>;
        headlineTypography?: StyledProp<ModalProps>;
        titleButton?: StyledProp<ModalProps>;
        crossButton?: StyledProp<ModalProps>;
    };
    /** Defines the max-width of the modal, as one of the preset values. Overridden by `size` prop. */
    sizeVariant?: SizeVariant;
    /** Defines the max-width of the modal, as the number of pixels. If provided, `sizeVariant` will be ignored. */
    size?: number;
    /** Props passed into the Modal's close button.
     * @themable */
    closeButtonProps?: ButtonPresentationProps;
    /** Props passed into the icon in the Modal's close button.
     * @themable */
    closeButtonIconProps?: IconPresentationProps;
    /** Props passed into the Modal's headline Typography component if `headline` is a string.
     * Ignored if `headline` is a node.
     * @themable */
    headlineTypographyProps?: TypographyPresentationProps;
    /** Properties governing the transition of the modal.
     * @themable */
    transition?: Transition;
}

type ModalOwnProps = MobiusStyledComponentProps<
    "div",
    {
        /**
         * Governs whether the overlay can be closed, if false, the 'x' button will not appear,
         * and the props `closeOnScrimClick` and `closeOnEsc` will be set to false.
         * Both props can also be managed independently.
         * */
        isCloseable?: boolean;
        /** whether the modal is an alertdialog (for use in cases where the dialog requires immediate attention). */
        alert?: boolean;
        /** Content to render as modal headline.  A string is automatically wrapped in Typography. */
        headline?: React.ReactNode;
    }
>;

export type ModalProps = ModalPresentationProps &
    ModalOwnProps &
    Omit<OverlayComponentProps, "isCloseable" | "zIndexLevel" | "titleId">;


const crossButton = (cssOverrides: any) => css`
    &:hover {
        background: none;
        border: none;
    }
`;

const ModalTitle = styled.div<InjectableCss<any>>`
    border-bottom: 1px solid ${getColor("common.border")};
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 30px;
    flex: 0 0 auto;
    ${injectCss}
`;

const ModalContent = styled.div<InjectableCss<any>>`
    padding: 30px;
    overflow-y: visible;
    ${injectCss}
`;

const modalContainerStyles = (cssOverrides: any) => css`
    margin-top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    border-radius: 6px
    animation: ${({ isClosing, transition }: { transition: Transition; isClosing: boolean }) => css`
        ${isClosing ? transition?.overlayExitKeyframes : transition?.overlayEntryKeyframes} ${transition?.length}ms
    `};
    .close-modal-button{
        background: none;
		border:none;
    }
    ${cssOverrides}

    > div {
        border-radius: 6px;
    }
`;

type ModalBodyProps = ThemeProps<BaseTheme> & Pick<ModalPresentationProps, "sizeVariant"> & { _size: number };

const modalBodyStyles = (cssOverrides: any, size?: number, sizeVariant?: SizeVariant) => css`
    width: 100%;
    background: ${getColor("common.background")};
    overflow-y: auto;
    flex: 0 1 auto;
    display: flex;
    position: relative;
    flex-direction: column;
    &:focus {
        outline: none;
    }
    ${({ theme }: ModalBodyProps) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    margin: 0;
                    max-width: 100%;
                `,
                null,
                null,
                css`
                    height: auto;
                    max-height: calc(100% - 90px);
                    margin: 45px;
                    max-width: 625px;
                    box-shadow: ${theme?.shadows[3]};
                `,
                null,
            ],
            "min",
        )};
    ${cssOverrides}
`;

/**
 * Modal is an overlay that handles its own visibility, and manages app focus based on its visibility.
 */
Overlay.setAppElement("body");
const CustomADIModal: React.FC<ModalProps> = withTheme(props => {
    if (typeof window === "undefined") {
        return null;
    }
    const {
        alert,
        children,
        handleClose,
        isOpen,
        isCloseable,
        size,
        sizeVariant,
        headline,
        headlineTypographyProps,
        theme,
        ...otherProps
    } = props;
    const { spreadProps } = applyPropBuilder(props, { component: "modal" });
    const cssOverrides = spreadProps("cssOverrides");
    const transition = spreadProps("transition");

    let headlineComponent: React.ReactElement = <div />;
    if (typeof headline === "string") {
        headlineComponent = (
            <Typography variant="h4" {...spreadProps("headlineTypographyProps")} css={cssOverrides?.headlineTypography}>
                {headline}
            </Typography>
        );
    } else if (headline) {
        headlineComponent = headline;
    }

    let titleButton;
    if (isCloseable) {
        titleButton = (
            <Button
                id="CloseModalButton"
                onClick={handleClose}
                aria-labelledby="close-modal"
                buttonType="outline"
                shape="pill"
                size={36}
                css={cssOverrides?.crossButton}
                {...spreadProps("closeButtonProps")}
                data-test-selector="modalCloseButton"
                className="close-modal-button"
            >
                {/* <ButtonIcon {...spreadProps("closeButtonIconProps")} /> */}
                <CloseModalIcon />
                <VisuallyHidden id="close-modal">{theme.translate("Close modal")}</VisuallyHidden>
            </Button>
        );
    }

    return (
        <Overlay
            {...theme.modal.defaultProps}
            handleClose={handleClose}
            role={alert ? "alertdialog" : "dialog"}
            isOpen={isOpen}
            isCloseable={isCloseable}
            transition={transition}
            cssOverrides={{
                ...cssOverrides,
                contentContainer: modalContainerStyles(cssOverrides.modalContainer),
                contentBody: modalBodyStyles(cssOverrides.modalBody, size, sizeVariant),
            }}
            titleId="modalTitle"
            zIndexLevel="modal"
            style={{borderRadius: '6px'}}
            {...omitMultiple(otherProps, ["cssOverrides", "transition"])}
        >
           
            <ModalContent css={cssOverrides?.modalContent}  style={{borderRadius: '6px'}}>
                <Typography id="modalTitle" css={cssOverrides?.modalTitle} style={{position: 'absolute', right: '20px', top: '35px', zIndex:1}}>
                    {titleButton}
                </Typography>
                {children}
            </ModalContent>
        </Overlay>
    );
});

CustomADIModal.defaultProps = {
    isOpen: false,
    isCloseable: true,
};

/** @component */
export default CustomADIModal;
